class StickyHeaderElements extends HTMLElement {
  static get observedAttributes() {
    return ["data-height-changed"];
  }

  connectedCallback() {
    this.headerInnerContainers = [...this.querySelectorAll('.header__inner-container')];

    // Set the style properties
    this.setStyleProperties();
    // Set the style properties on resize
    window.addEventListener('resize', () => this.setStyleProperties());
    // Throttle the resize event to 600ms to also call it after header transitions
    window.addEventListener('resize', throttle(() => this.setStyleProperties(), 600));
  }

  attributeChangedCallback(name) {
    if (!name === 'data-height-changed') return;
    this.setStyleProperties();
  }

  /**
 * Computes the height of the element by summing up the height of all its children.
   * @returns {string} The computed height as a string with 'px' unit.
   */
  computedHeight() {
    // Reset height before calculating
    let height = 0;

    // Sum up the height of all children
    [...this.children].forEach(element => {
      const {height: elementHeight} = element.getBoundingClientRect();
      const elementName = element.id.replace('shopify-section-', '');

      height += elementHeight;
      document.documentElement.style.setProperty(`--${elementName}-height`, `${elementHeight}px`);
    });

    // Return the computed height as a string with 'px' unit
    return `${height}px`;
  }

  /**
   * Sets the height of the sticky header elements to the referred style properties
   */
  setStyleProperties() {
    this.style.height = this.computedHeight();
  }
}

class CollapsibleHeader extends Collapsible {
  constructor() {
    super();
    this.header = this.closest('[data-header]');
    this.headerWrapper = this.closest('#shopify-section-header');
    if (this.header) {
      this.header.nextElementSibling?.classList.contains('header-shadow') ? this.shadow = this.header.nextElementSibling : '';

      // extra events
      this.shadow?.addEventListener('click', this.closeAll.bind(this));
    }
  }

  events() {
    if (!this.header) {
      super.events();
    }
  }

  open(group) {
    super.open(group);
    if (this.headerWrapper) {
      this.headerWrapper.classList.add('shopify-section-header--open');
    }
    this.toggleHeaderClass(true);
  }

  close(group) {
    super.close(group);
    if (this.headerWrapper) {
      this.headerWrapper.classList.remove('shopify-section-header--open');
    }
    this.toggleHeaderClass(false);
  }

  closeSiblings(currentGroup = null) {
    super.closeSiblings(currentGroup);
    this.toggleHeaderClass(false);
  }

  openAll() {
    super.openAll();
    this.toggleHeaderClass(true);
  }

  closeAll() {
    super.closeAll();
    this.toggleHeaderClass(false);
  }

  toggleHeaderClass(open = true) {
    if (!this.header) {
      return false;
    }

    if (open) {
      // set header open
      this.header.classList.add('header--is-open');
    } else {
      // set header closed if all menu items are closed
      let close = true;
      this.groups.forEach((group) => {
        if (group.classList.contains(this.options.toggleClass)) {
          close = false;
        }
      });
      if (close) {
        this.header.classList.remove('header--is-open');
      }
    }
  }
}

class ToggleMobileNavigation extends ToggleElementClass {
  constructor() {
    super();
    this.header = this.closest('[data-header]');
    this.headerWrapper = this.closest('#shopify-section-header');
    this.toggleClassSecondary = this.options.toggleClassSecondary; // toggleClassSecondary used to distinguish the menu opening from the hamburger or search
    if (this.header) {
      this.header.nextElementSibling?.classList.contains('header-shadow') ? this.shadow = this.header.nextElementSibling : '';

      // iPhone fix
      if (navigator.platform === 'iPhone') {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }

      // extra events
      document.addEventListener('keyup', this.onKeyUp.bind(this));
      this.shadow?.addEventListener('click', this.remove.bind(this));
    }
  }

  events() {
    if (!this.header) {
      super.events();
    }
  }

  onKeyUp(event) {
    if (!this.header.classList.contains('header--is-open')) {
      return;
    }
    if(event?.code.toUpperCase() !== 'ESCAPE') {
      return;
    }
    const { toggleClass } = this.options;
    if (this.target?.classList.contains(toggleClass)) {
      this.remove();
      // remove the menu & search classes when closing the navigation
      this.target.classList.remove('menu-drawer--menu-active','menu-drawer--search-active');
    }
  }

  add() {
    super.add();
    if (this.header) {
      this.header.classList.add('header--is-open');
    }
    if (this.headerWrapper) {
      this.headerWrapper.classList.add('shopify-section-header--open');
    }
    // add toggle class secondary
    this.toggleClassSecondary ? this.target.classList.add(this.toggleClassSecondary): '';

    // update aria expanded on menu open
    this.ariaExpanded = true;

    trapFocus(this.header);
  }

  remove() {
    super.remove();
    if (this.header) {
      this.header.classList.remove('header--is-open');
    }
    if (this.headerWrapper) {
      this.headerWrapper.classList.remove('shopify-section-header--open');
    }
    // remove toggle class secondary
    this.toggleClassSecondary ? this.target.classList.remove(this.toggleClassSecondary): '';

    // update aria expanded on menu close
    this.header.querySelectorAll('[data-open-mobile-navigation]').forEach(button => button.ariaExpanded = false);

    // remove the menu & search classes when closing the navigation
    if (this.hasAttribute('data-close-mobile-navigation')) {
      this.target.classList.remove('menu-drawer--menu-active','menu-drawer--search-active');
    }

    this.closeAllSibblings();
    removeTrapFocus();
  }

  closeAllSibblings() {
    const collapsibles = this.target.querySelector('collapsible-element');
    collapsibles.closeAll();
  }
}


class ToggleSearch extends ToggleElementClass {
  constructor() {
    super();
  }

  construct() {
    super.construct();

    // elements
    this.header = this.closest('[data-header]');

    // options
    const { toggleClass, target } = this.options;

    // key up escape
    document.addEventListener('keyup', this.onKeyUp.bind(this));

    // custom close button
    this.closeButton = this.target?.querySelector('[data-close-button]');
    this.closeButton?.addEventListener('click', (e) => {
      this.preventDefault(e);
      this.stopPropagation(e);
      this.remove();
    });

    // Remove classes incase the header navigation drawer is open.
    this.addEventListener('click', () => {
      document.querySelector('#header')?.classList.remove('header--is-open');
      document.querySelector('[data-menu-drawer]')?.classList.remove('menu-drawer--is-active');
    });

    // click outside search
    document.addEventListener("click", (e) => {
      // if search is open
      if (this.target?.classList.contains(toggleClass)) {
        const isOutside = !e.target.closest(target);
        if (isOutside) {
          this.remove();
        }
      }
    });
  }

  onKeyUp(event) {
    if (!this.header.classList.contains('header--search-is-open')) {
      return;
    }
    if(event?.code.toUpperCase() !== 'ESCAPE') {
      return;
    }
    const { toggleClass } = this.options;
    if (this.target?.classList.contains(toggleClass)) {
      this.remove();
    }
  }

  add() {
    super.add();
    if (this.header) {
      this.header.classList.add('header--search-is-open');
    }
    if (this.target) {
      trapFocus(this.target);
    }
  }

  remove() {
    super.remove();
    if (this.header) {
      this.header.classList.remove('header--search-is-open');
    }
    removeTrapFocus();
  }

}

class StickyHeader extends HTMLElement {
  constructor() {
    super();

    // select element
    this.header = document.querySelector('[data-header]');
    this.headerWrapper = document.querySelector('#shopify-section-header');
    this.headerElement = this;
    this.headerNav = this.querySelector('collapsible-header');
    this.mobileNav = this.querySelector('toggle-mobile-navigation');
    this.search = this.querySelector('[data-header-search]');
    this.searchToggle = this.querySelector('toggle-search');
    this.stickyHeaderElements = document.querySelector('sticky-header-elements');

    if (this.headerWrapper) {
      // scroll parameters
      this.currentScrollTop = 0;
      this.scrollOffset = 0;
      this.headerStart = 0;

      if (this.headerElement) {
        this.scrollOffset += this.headerElement.offsetHeight;
        // set Header height in css, since the child has position fixed (height 0), so the rest of the content is pushed down
        window.addEventListener('resize', () => {
          // update the Header height when the window resizes
          if (window.innerWidth > 1024) {
            clearTimeout(this.timer);
            this.timer = setTimeout(() => {
              this.scrollOffset = 0;
              this.scrollOffset += this.headerElement.offsetHeight;
            }, 50);
          }
        }, false);
      }

      // scroll event
      this.raf = null; // Safe request animation frame
      this.timer = null, // Safe scroll timer
      window.addEventListener('scroll', () => {
        clearTimeout(this.timer);
        if (!this.raf) {
          // A request animation frame is used to animate on max 60fps.
          this.raf = requestAnimationFrame(this.scrollAnimation.bind(this));
        }
        this.timer = setTimeout(() => {
          // Stop animation, cancel request animation frame
          cancelAnimationFrame(this.raf);
          this.raf = null;
        }, 50);
      }, false);

    }
  }

  scrollAnimation() {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;

    // only animate if header is closed.
    if (!this.header?.classList?.contains('header--is-open')) {
      if((scrollTop + window.innerHeight) > document.body.clientHeight) {
        this.decreaseHeader();
      } else if (scrollTop > this.currentScrollTop && scrollTop > this.scrollOffset) {
        this.decreaseHeader();
      } else if (scrollTop <= this.headerStart) {
        this.increaseHeaderTop();
      } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerStart) {
        this.increaseHeader();
      }
    }

    this.currentScrollTop = scrollTop;

    // Recall request animation loop
    this.raf = requestAnimationFrame(this.scrollAnimation.bind(this));
  }

  decreaseHeader() {
    this.headerWrapper.classList.add('shopify-section-header--sticky', 'shopify-section-header--animate');
    this.closeHeaderNav();
    this.closeMobileNav();
    this.closeSearch();
    setTimeout(() => {
      this.stickyHeaderElements?.computedHeight();
    }, 400);
  }

  increaseHeader() {
    this.headerWrapper.classList.add('shopify-section-header--sticky');
    this.headerWrapper.classList.remove('shopify-section-header--animate');
    setTimeout(() => {
      this.stickyHeaderElements?.computedHeight();
    }, 400);
  }

  increaseHeaderTop() {
    this.headerWrapper.classList.remove('shopify-section-header--sticky', 'shopify-section-header--animate');
    setTimeout(() => {
      this.stickyHeaderElements?.computedHeight();
    }, 400);
  }

  closeHeaderNav() {
    if (!this.headerNav || window.innerWidth < 1024) {
      return false;
    }
    this.headerNav.closeAll();
  }

  closeMobileNav() {
    if(!this.mobileNav) {
      return false;
    }
    this.mobileNav.remove();
  }

  closeSearch() {
    if (!this.search || !this.searchToggle) {
      return false;
    }
    if (this.search.classList.contains('header__search--is-active')) {
      this.searchToggle.remove();
    }
  }

}

if (!customElements.get('sticky-header-elements')) {
  customElements.define('sticky-header-elements', StickyHeaderElements);
}

if (!customElements.get('collapsible-header')) {
  customElements.define('collapsible-header', CollapsibleHeader);
}

if (!customElements.get('toggle-mobile-navigation')) {
  customElements.define('toggle-mobile-navigation', ToggleMobileNavigation);
}

if (!customElements.get('toggle-search')) {
  customElements.define('toggle-search', ToggleSearch);
}

if (!customElements.get('sticky-header')) {
  customElements.define('sticky-header', StickyHeader);
}
